import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import {
  ChartBar,
  ChartLine,
  Database,
  DotsThree,
  Download,
  FlowArrow,
  Sparkle,
  X,
} from '@phosphor-icons/react';

interface MobileNavigationProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  selectedDatasetCount: number;
  hasQualityReports: boolean;
  hasCorrelations: boolean;
}

const navigationItems = [
  {
    id: 'templates',
    label: 'Get Started',
    icon: Sparkle,
    description: 'Research templates',
  },
  {
    id: 'discover',
    label: 'Discover',
    icon: Database,
    description: 'Browse data sources',
  },
  {
    id: 'quality',
    label: 'Quality',
    icon: ChartLine,
    description: 'Data quality reports',
  },
  {
    id: 'correlations',
    label: 'Analyze',
    icon: ChartLine,
    description: 'Find correlations',
  },
  {
    id: 'transform',
    label: 'Transform',
    icon: FlowArrow,
    description: 'Data pipelines',
  },
  {
    id: 'visualize',
    label: 'Visualize',
    icon: ChartBar,
    description: 'Create charts',
  },
  { id: 'export', label: 'Export', icon: Download, description: 'Export data' },
];

export function MobileNavigation({
  activeTab,
  onTabChange,
  selectedDatasetCount,
  hasQualityReports,
  hasCorrelations,
}: MobileNavigationProps) {
  const [isOpen, setIsOpen] = useState(false);

  const getTabStatus = (tabId: string) => {
    switch (tabId) {
      case 'quality':
        return hasQualityReports
          ? 'complete'
          : selectedDatasetCount > 0
            ? 'available'
            : 'disabled';
      case 'correlations':
        return hasCorrelations
          ? 'complete'
          : selectedDatasetCount >= 2
            ? 'available'
            : 'disabled';
      case 'transform':
      case 'visualize':
      case 'export':
        return selectedDatasetCount > 0 ? 'available' : 'disabled';
      default:
        return 'available';
    }
  };

  const handleTabSelect = (tabId: string) => {
    onTabChange(tabId);
    setIsOpen(false);
  };

  return (
    <div className='md:hidden'>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button variant='outline' size='sm' className='gap-2'>
            <DotsThree className='w-4 h-4' />
            Menu
          </Button>
        </SheetTrigger>
        <SheetContent side='left' className='w-80 p-0'>
          <div className='flex items-center justify-between p-4 border-b'>
            <h2 className='text-lg font-semibold'>Navigation</h2>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setIsOpen(false)}
              className='h-8 w-8 p-0'
            >
              <X className='w-4 h-4' />
            </Button>
          </div>

          <div className='p-4 space-y-2'>
            {navigationItems.map(item => {
              const status = getTabStatus(item.id);
              const isActive = activeTab === item.id;
              const isDisabled = status === 'disabled';

              return (
                <Button
                  key={item.id}
                  variant={isActive ? 'default' : 'ghost'}
                  className='w-full justify-start gap-3 h-auto p-3'
                  onClick={() => handleTabSelect(item.id)}
                  disabled={isDisabled}
                >
                  <item.icon className='w-5 h-5 flex-shrink-0' />
                  <div className='flex-1 text-left'>
                    <div className='font-medium'>{item.label}</div>
                    <div className='text-xs text-muted-foreground'>
                      {item.description}
                    </div>
                  </div>
                  {status === 'complete' && (
                    <Badge variant='secondary' className='text-xs'>
                      ✓
                    </Badge>
                  )}
                </Button>
              );
            })}
          </div>

          {selectedDatasetCount > 0 && (
            <div className='mt-auto p-4 border-t bg-muted/50'>
              <div className='text-sm font-medium mb-1'>Progress</div>
              <div className='text-xs text-muted-foreground'>
                {selectedDatasetCount} dataset
                {selectedDatasetCount !== 1 ? 's' : ''} selected
              </div>
              <div className='mt-2 w-full bg-background rounded-full h-2'>
                <div
                  className='bg-primary h-2 rounded-full transition-all duration-300'
                  style={{
                    width: `${Math.min(
                      100,
                      (selectedDatasetCount > 0 ? 25 : 0) +
                        (hasQualityReports ? 25 : 0) +
                        (hasCorrelations ? 25 : 0) +
                        (activeTab === 'export' ? 25 : 0)
                    )}%`,
                  }}
                />
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
